import { isDocumentFormatValid } from 'utils/document.util'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { addCustomIssue } from 'utils/schema.util'
import { isFilled, validateField } from 'utils/validate.util'
import { z } from 'zod'
import { DocumentTypeId } from 'utils/enums/document-type.enum'

const NOT_IN_TYPES_DOCUMENT: string[] = [
  DocumentTypeId.NONE,
  DocumentTypeId.CERTIDAO_DE_NASCIMENTO,
  DocumentTypeId.CERTIDAO_DE_OBITO,
]

// v1

export const documentSchema = z
  .object({
    id: z.number().nullish(),
    arquivoBase64: z.string().nullish(),
    arquivoNome: z.string().nullish(),
    arquivoTipo: z.string().nullish(),
    descricaoIdTipoDocumento: z.string().nullish(),
    idTipoDocumento: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    numeroDocumento: z.string().nullish(),
    matricula: z.string().nullish(),
    livro: z.string().nullish(),
    folha: z.string().nullish(),
    termo: z.string().nullish(),
    remover: z.boolean().optional(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)
    const typeId = fields.idTipoDocumento
    const documentNumber = fields.numeroDocumento

    if (DocumentTypeId.NONE === typeId && isFilled(documentNumber)) {
      addIssue('idTipoDocumento', SystemMessages.MESSAGE_002)
    } else if (typeId && !NOT_IN_TYPES_DOCUMENT.includes(typeId)) {
      if (documentNumber && isFilled(documentNumber)) {
        if (!isDocumentFormatValid(typeId, documentNumber)) {
          addIssue('numeroDocumento', SystemMessages.MESSAGE_030)
        }
      } else {
        addIssue('numeroDocumento', SystemMessages.MESSAGE_002)
      }
    }
  })

export type DocumentType = z.infer<typeof documentSchema>

export const documentInit: DocumentType = {
  descricaoIdTipoDocumento: '',
  idTipoDocumento: '0',
  numeroDocumento: '',
  matricula: '',
  livro: '',
  folha: '',
  termo: '',
  remover: false,
}

// v2

export const documentoV2Schema = z
  .object({
    id: z.number().nullish(),
    tipoDocumento: z.object({
      id: z.number().nullish(),
      nome: z.string().nullish(),
      descricao: z.string().nullish(),
    }),
    numeroDocumento: z.string().nullish(),
    descricaoDocumento: z.string().nullish(),
    matricula: z.string().nullish(),
    livro: z.string().nullish(),
    folha: z.string().nullish(),
    termo: z.string().nullish(),
    arquivoBase64: z.string().nullish(),
    arquivoTipo: z.string().nullish(),
    arquivoNome: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)
    const typeId: string = fields.tipoDocumento.id ? String(fields.tipoDocumento.id) : '0'
    const documentNumber = fields.numeroDocumento

    if (DocumentTypeId.NONE === typeId && isFilled(documentNumber)) {
      addIssue('idTipoDocumento', SystemMessages.MESSAGE_002)
    } else if (typeId && !NOT_IN_TYPES_DOCUMENT.includes(typeId)) {
      if (documentNumber && isFilled(documentNumber)) {
        if (!isDocumentFormatValid(typeId, documentNumber)) {
          addIssue('numeroDocumento', SystemMessages.MESSAGE_030)
        }
      } else {
        addIssue('numeroDocumento', SystemMessages.MESSAGE_002)
      }
    }
  })

export type DocumentV2Type = z.infer<typeof documentoV2Schema>

export const DocumentV2Init: DocumentV2Type = {
  tipoDocumento: {
    id: 0,
    descricao: '',
  },
  arquivoBase64: '',
  arquivoNome: '',
  arquivoTipo: '',
  numeroDocumento: '',
  folha: '',
  livro: '',
  termo: '',
  matricula: '',
  id: null,
}
